import moment from 'moment';
import * as React from 'react';

import EmptyDiagnosticProgressReport from './empty_diagnostic_progress_report.jsx';
import GrowthSummarySection from './growthSummarySection';
import { Classroom, Diagnostic } from './interfaces';
import { baseDiagnosticImageSrc, calculateClassGrowthPercentage, } from './shared';
import { ASSIGN_GROWTH_DIAGNOSTIC_SUB_PAGE, } from './sidebar';

import { requestGet } from '../../../../../../modules/request/index';
import { DropdownInput, Tooltip, EXTRA_SMALL_ICON_BASE_SRC, getPageTitle, extraSmallGroupIcon, } from '../../../../../Shared/index';
import { DIAGNOSTICS_FEATURED_BLOG_POST_ID, } from '../../../../constants/featuredBlogPost';
import ArticleSpotlight from '../../../shared/articleSpotlight';
import DemoOnboardingTour, { DEMO_ONBOARDING_DIAGNOSTIC_ACTIVITY_PACKS_INDEX, } from '../../../shared/demo_onboarding_tour';
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from '../../progress_report_constants';

const calendarDateIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/calendar-date.svg`
const userMultipleIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/user-multiple.svg`

const multipleCardsIcon = <img alt="Activity pack icon" src={`${baseDiagnosticImageSrc}/icons-card-multiple.svg`} />
const multipleUsersIcon = <img alt="Multiple user icon" src={userMultipleIconSrc} />
const calendarDateIcon = <img alt="" src={calendarDateIconSrc} />

const MOBILE_WIDTH = 990
const AVERAGE_FONT_WIDTH = 5
const ACTIVITY_PACK_TEXT_MAX_WIDTH = 264

const ALL = 'ALL'
const ALL_OPTION = { label: 'All Classrooms', value: ALL }

function summaryLink(isPostDiagnostic, activityId, classroomId, unitId) {
  const summaryPath = isPostDiagnostic ? 'growth_summary' : 'summary'
  const baseSummaryLink = `/teachers/progress_reports/diagnostic_reports/diagnostics/${activityId}/classroom/${classroomId}/${summaryPath}`
  return unitId ? `${baseSummaryLink}?unit=${unitId}` : baseSummaryLink
}

const AssignedSection = ({ activity, sectionTitle, isPostDiagnostic, }) => {
  const { assigned_date, unit_name, completed_count, assigned_count, activity_id, classroom_id, unit_id, } = activity
  const activityPackText = `Activity pack: ${unit_name}`
  let activityPackElement = <span>{activityPackText}</span>
  if (window.innerWidth > MOBILE_WIDTH && ((activityPackText.length * AVERAGE_FONT_WIDTH) > ACTIVITY_PACK_TEXT_MAX_WIDTH)) {
    activityPackElement = <Tooltip tooltipText={activityPackText} tooltipTriggerText={activityPackText} tooltipTriggerTextClass="activity-pack-name" />
  }
  return (
    <section className="pre">
      <div>
        <h4>{sectionTitle}</h4>
        <p>{calendarDateIcon}<span>Assigned: {moment(assigned_date).format('MMM D, YYYY')}</span></p>
        {unit_name && <p>{multipleCardsIcon}{activityPackElement}</p>}
        <p>{multipleUsersIcon}<span>Completed: {completed_count} of {assigned_count}</span></p>
      </div>
      <div>
        <a className="diagnostic-reports-button quill-button extra-small grey outlined focus-on-light" href={summaryLink(isPostDiagnostic, activity_id, classroom_id, unit_id)}>View results and recommendations</a>
      </div>
    </section>
  )
}

const PostSection = ({ post, activityId, preActivityId, preUnitId, classroomId, }) => {
  if (post) {
    return <AssignedSection activity={post} isPostDiagnostic={true} sectionTitle="Post" />
  }

  const unitQueryString = preUnitId ? `?unit=${preUnitId}` : ''
  const assignGrowthDiagnosticLink = `/teachers/progress_reports/diagnostic_reports/diagnostics/${preActivityId}/classroom/${classroomId}/${ASSIGN_GROWTH_DIAGNOSTIC_SUB_PAGE}${unitQueryString}`

  return (
    <section className="post">
      <div>
        <h4>Post</h4>
        <p>{calendarDateIcon}<span>Not assigned</span></p>
      </div>
      <div className="post-buttons-container">
        <a className="diagnostic-reports-button quill-button extra-small grey outlined focus-on-light" href={`/activity_sessions/anonymous?activity_id=${activityId}`} rel="noopener noreferrer" target="_blank">Preview</a>
        <a className="diagnostic-reports-button quill-button extra-small grey outlined focus-on-light" href={assignGrowthDiagnosticLink}>Assign</a>
      </div>
    </section>
  )
}

const Diagnostic = ({ diagnostic, }) => {
  const [studentResults, setStudentResults] = React.useState<StudentResult[]>([]);
  const [skillGroupSummaries, setSkillGroupSummaries] = React.useState<SkillGroupSummary[]>([]);
  const [classwideGrowthAverage, setClasswideGrowthAverage] = React.useState<number>(null);
  const { name, pre, post, } = diagnostic
  const completedStudentCount = studentResults.filter(sr => sr.skill_groups).length

  React.useEffect(() => {
    if (post && post.activity_id) {
      getResults()
    }
  }, [])

  React.useEffect(() => {
    // classwideGrowthAverage result may be 0 in some instances so we check for initial null value
    if (skillGroupSummaries.length && completedStudentCount && classwideGrowthAverage === null) {
      calculateClassGrowthPercentage({ skillGroupSummaries, completedStudentCount, setClasswideGrowthAverage })
    }
  }, [skillGroupSummaries])

  function getResults() {
    requestGet(`/teachers/progress_reports/diagnostic_growth_results_summary?activity_id=${post.activity_id}&classroom_id=${pre.classroom_id}`,
      (data) => {
        setStudentResults(data.student_results);
        setSkillGroupSummaries(data.skill_group_summaries);
      }
    )
  }

  let postAndGrowth;

  if (pre.post_test_id) {
    const growthSummaryLink = summaryLink(true, pre.post_test_id, pre.classroom_id, pre.unit_id)

    // we only care about whether or not the pre-diagnostic is eligible because even if the post- is, we can't compare the data and so need to keep the empty state
    const eligibleForQuestionScoring = pre.eligible_for_question_scoring

    if (post.assigned_count) {
      postAndGrowth = (<React.Fragment>
        <PostSection post={post} />
        <GrowthSummarySection eligibleForQuestionScoring={eligibleForQuestionScoring} growthSummaryLink={growthSummaryLink} showGrowthSummary={true} skillsGrowth={classwideGrowthAverage} />
      </React.Fragment>)
    } else {
      postAndGrowth = (<React.Fragment>
        <PostSection activityId={pre.post_test_id} classroomId={pre.classroom_id} preActivityId={pre.activity_id} preUnitId={pre.unit_id} />
        <GrowthSummarySection eligibleForQuestionScoring={eligibleForQuestionScoring} name={name} />
      </React.Fragment>
      )
    }
  }

  return (
    <section aria-label={name} className="diagnostic">
      <div className="name"><h3>{name}</h3></div>
      <div className="pre-and-post-wrapper">
        <AssignedSection activity={pre} isPostDiagnostic={false} sectionTitle="Pre" />
        {postAndGrowth}
      </div>
    </section>
  )
}

const Classroom = ({ classroom, }) => {
  const diagnostics = classroom.diagnostics.map(d => <Diagnostic diagnostic={d} key={`${d.pre.activity_id}-${classroom.id}`} />)
  return (
    <section aria-labelledby={classroom.name} className="classroom-section" key={classroom.id}>
      <h2 id={classroom.name}>{classroom.name}</h2>
      {diagnostics}
    </section>
  )
}

const DiagnosticActivityPacks = ({ classrooms, }) => {
  document.title = getPageTitle('My Diagnostics', 'View Reports')
  const selectedClassroom = classrooms.find(c => Number(c.id) === Number(window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID)))
  const [selectedClassroomId, setSelectedClassroomId] = React.useState(selectedClassroom && selectedClassroom.id || ALL)

  function onClassesDropdownChange(e) {
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, e.value)
    setSelectedClassroomId(e.value)
  }

  if (!classrooms.length) { return <EmptyDiagnosticProgressReport /> }

  const dropdownOptions = [ALL_OPTION].concat(classrooms.map(c => ({ value: c.id, label: c.name, })))
  const classroomElements = selectedClassroomId === ALL ? classrooms.map(c => <Classroom classroom={c} key={c.id} />) : <Classroom classroom={classrooms.find(c => c.id === selectedClassroomId)} />

  return (
    <React.Fragment>
      <main className="diagnostic-activity-packs-container white-background-accommodate-footer">
        <DemoOnboardingTour pageKey={DEMO_ONBOARDING_DIAGNOSTIC_ACTIVITY_PACKS_INDEX} />
        <div className="container diagnostic-activity-packs">
          <header>
            <h1>My Diagnostics</h1>
            <DropdownInput
              ariaLabel="Classroom"
              className="small bordered"
              customMenuIconUrl={extraSmallGroupIcon.src}
              handleChange={onClassesDropdownChange}
              id="classroom-dropdown"
              isSearchable={false}
              options={dropdownOptions}
              value={dropdownOptions.find(opt => opt.value === selectedClassroomId)}
            />
          </header>
          {classroomElements}
        </div>
      </main>
      <ArticleSpotlight blogPostId={DIAGNOSTICS_FEATURED_BLOG_POST_ID} />
    </React.Fragment>
  )
}

export default DiagnosticActivityPacks
